import { useCallback, useState } from "react";
import { useParams } from "react-router";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { ResourceChangeProgressNotification } from "../../../../api/types/resourceChangesTypes";
import useClientContext from "../../../../context/useClientContext";
import useResourceChanged from "../../../../hooks/useResourceChanged";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import FullPageStatusProgress from "../FullPageStatusProgress";
import { useRollbackDataImport } from "../dataImportOperations";
import type { OperationStatus } from "../importDataPagesTypes";
import { getPermissionsToManageImport, isImportOperationRunning } from "../importDataStateHelper";
import ImportDataDetailsActions from "./ImportDataDetailsActions";
import ImportDataDetailsSection from "./ImportDataDetailsSection";
import ImportDataDetailsTitle from "./ImportDataDetailsTitle";

const ImportDataDetailsPage = () => {
  usePageTitle("Data Import");

  const { id } = useParams();
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [rollbackStatus, setRollbackStatus] = useState<OperationStatus>();
  const [liveProgress, setLiveProgress] = useState<ResourceChangeProgressNotification>();

  const getDetails = useCallback(() => adminApi.getDataImportDetails(defined(id)), [id]);
  const getSourceFiles = useCallback(() => adminApi.getDataImportSourceFiles(defined(id)), [id]);

  const [dataImportDetails, fetchDetailsError, { fetch: fetchDetails, fetchSilent: fetchDetailsSilent, isFetching }] =
    useFetch(getDetails);
  const [sourceFiles, fetchFilesError] = useFetch(getSourceFiles);

  const handleRollbackStatusUpdate = useCallback((status: OperationStatus) => setRollbackStatus(status), []);

  const handleRollbackComplete = useCallback(() => {
    setRollbackStatus(undefined);
    sendNotification("Data import rollback has been completed successfully");
    fetchDetails();
  }, [sendNotification, fetchDetails]);

  const handleRollbackError = useCallback(
    (error: ApiError) => {
      setRollbackStatus(undefined);
      logError(error, "[ImportDataDetailsPage] rollbackImport");
      sendNotificationError(`Error occurred when applying data import rollback: ${error.message}`);
    },
    [sendNotificationError]
  );

  const startRollbackDataImport = useRollbackDataImport({
    onStatusUpdate: handleRollbackStatusUpdate,
    onComplete: handleRollbackComplete,
    onError: handleRollbackError,
  });

  const handleDataImportChanged = async () => {
    const error = await fetchDetailsSilent();
    if (error) {
      logError(error, "[ImportDataDetailsPage] fetchDetailsSilent");
    }

    setLiveProgress(undefined);
  };

  useResourceChanged("DataImport", defined(id), {
    onChanged: handleDataImportChanged,
    onProgress: setLiveProgress,
    isOperationRunning: dataImportDetails !== undefined && isImportOperationRunning(dataImportDetails),
  });

  if (fetchDetailsError || fetchFilesError) {
    logError(fetchDetailsError || fetchFilesError, "[ImportDataDetailsPage]");
    return <DataLoadingFailed title="Loading data import details failed" />;
  }

  if (dataImportDetails === undefined || sourceFiles === undefined || isFetching) {
    return <InlineLoader />;
  }

  if (rollbackStatus !== undefined) {
    return (
      <FullPageStatusProgress
        title="We are applying the rollback. This may take some time."
        operationStatus={rollbackStatus}
      />
    );
  }

  const handleRollback = () => {
    startRollbackDataImport(dataImportDetails.id);
  };

  const hasPermissionsToManage = hasPermissions(getPermissionsToManageImport(dataImportDetails.entriliaProductAreas));

  return (
    <>
      <GeneralPageHeader
        TitleComponent={<ImportDataDetailsTitle dataImportDetails={dataImportDetails} />}
        showDefaultBackButtonTo={`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}`}
      >
        <ImportDataDetailsActions
          showRollback={
            hasPermissionsToManage &&
            dataImportDetails.rollbackCreated &&
            dataImportDetails.state === "Applied" &&
            !isImportOperationRunning(dataImportDetails)
          }
          importTarget={dataImportDetails.files.length === 1 ? dataImportDetails.files[0]?.importTypeTarget : undefined}
          onRollback={handleRollback}
        />
      </GeneralPageHeader>
      <ImportDataDetailsSection
        dataImport={dataImportDetails}
        sourceFiles={sourceFiles}
        progress={isImportOperationRunning(dataImportDetails) ? liveProgress : undefined}
        onRefresh={fetchDetails}
      />
    </>
  );
};

export default ImportDataDetailsPage;
